import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Sparkle } from "@phosphor-icons/react";
import TemplatesTable from "@/pages/admin-templates/TemplatesTable";
import NewTemplateDialog from "@/pages/admin-templates/NewTemplateDialog";

const EMPTY_FORM = { title: "", summary: "", category: "business", price_usd: 12, content: "", ai_seed: false };

export default function AdminTemplates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => {
    api.get("/admin/templates").then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const onError = (e) => toast.error(e.response?.data?.detail || "Failed");

  const create = async (e) => {
    e.preventDefault();
    if (!form.title || !form.summary) { toast.error("Title and summary required"); return; }
    if (!form.ai_seed && !form.content.trim()) {
      toast.error("Either provide content or enable AI seed.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/templates", { ...form, price_usd: Number(form.price_usd) });
      toast.success("Template created");
      setOpen(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) { onError(e); } finally { setBusy(false); }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await api.delete(`/admin/templates/${t.id}`); load(); toast.success("Deleted"); }
    catch (e) { onError(e); }
  };

  const toggleStatus = async (t) => {
    const next = t.status === "published" ? "draft" : "published";
    try { await api.patch(`/admin/templates/${t.id}`, { status: next }); load(); }
    catch (e) { onError(e); }
  };

  const seedAll = async () => {
    if (!window.confirm("AI-seed 5 starter business templates? This calls Claude Sonnet 4.5 and may take 1-2 minutes.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/templates/seed");
      toast.success(`Seeded: ${data.ids?.length || 0} templates`);
      load();
    } catch (e) { onError(e); } finally { setSeeding(false); }
  };

  return (
    <div className="max-w-7xl">
      <div className="gf-overline">Admin · Templates</div>
      <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">
        Marketplace catalog.
      </h1>
      <p className="text-zinc-600 mt-3 max-w-2xl text-sm leading-relaxed">
        Manage business-startup templates ($10–$15). Create manually or let Claude Sonnet 4.5 seed
        the content from a topic + summary.
      </p>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={() => setOpen(true)} data-testid="tpl-new-btn"
          className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
          <Plus size={16} className="mr-2" /> New template
        </Button>
        <Button onClick={seedAll} disabled={seeding} variant="outline" data-testid="tpl-seed-btn"
          className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">
          <Sparkle size={16} className="mr-2" /> {seeding ? "Seeding…" : "AI-seed 5 starters"}
        </Button>
      </div>

      <ModerationQueueRemoved />

      <TemplatesTable
        items={items}
        onToggleStatus={toggleStatus} onDelete={del}
      />

      <NewTemplateDialog
        open={open} onOpenChange={setOpen}
        form={form} setForm={setForm}
        busy={busy} onSubmit={create}
      />
    </div>
  );
}

function ModerationQueueRemoved() { return null; }
